import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import {
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  Clock3,
  Flame,
  Gift,
  History,
  RotateCcw,
  UserRound,
  UsersRound,
} from 'lucide-react';
import { formatAccountPoints } from '../points/format';
import { useCoinModal } from '../arena/CoinModal';
import { Modal } from './Modal';
import { PointsAmount, PointsText } from '../PointsAmount';
import './points.css';
import {
  dailyRewardForDay,
  DAY,
  formatPoints,
  SHARE_REWARD,
  streakLength,
  WELCOME_REWARD,
  type Reward,
} from './points-model';
import type { PointsPreview } from './usePointsPreview';
import type { AccountPoints } from '../points/useAccountPoints';
const number = new Intl.NumberFormat('en-US');
const activityDate = new Intl.DateTimeFormat('ru-RU', {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
});
const activityTime = new Intl.DateTimeFormat('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function XMark() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M18.9 2H22l-6.8 7.8L23.2 22h-6.3L12 14.6 5.5 22H2.3l7.3-8.5L.8 2h6.5l4.4 6.7L18.9 2Zm-1.1 18h1.7L6.4 3.9H4.6L17.8 20Z" />
    </svg>
  );
}

export type PointsDesignTask = {
  id: string;
  title: string;
  description: string;
  reward: number;
  icon: 'first-bet' | 'x' | 'profile' | 'invite';
  action: string;
};

const FLOAT_AFTER_ROWS = 10;

function useFloatingCollapse(
  open: boolean,
  anchor: RefObject<HTMLElement | null>,
  toggle: RefObject<HTMLButtonElement | null>,
  anchorKey: string | undefined,
) {
  const [floating, setFloating] = useState(false);
  useEffect(() => {
    if (!open || !anchor.current || !toggle.current) {
      setFloating(false);
      return;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      if (!anchor.current || !toggle.current) return;
      const bottomInset = Number.parseFloat(
        getComputedStyle(toggle.current).bottom,
      );
      const rowTitle =
        anchor.current.querySelector('h3, strong') ?? anchor.current;
      setFloating(
        rowTitle.getBoundingClientRect().top < window.innerHeight - bottomInset,
      );
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(anchor.current.closest('.rd-points') ?? anchor.current);
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [open, anchor, toggle, anchorKey]);
  return open && floating;
}

export function PointsPage({
  preview,
  account,
  environment = 'demo',
  designTasks = [],
}: {
  preview: PointsPreview;
  account?: AccountPoints;
  environment?: 'live' | 'demo';
  designTasks?: readonly PointsDesignTask[];
}) {
  const { state, dispatch } = preview;
  const connected = account ? !!account.data : state.connected;
  const [now, setNow] = useState(Date.now());
  const [modal, setModal] = useState<'share' | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);
  const pageRoot = useRef<HTMLElement>(null);
  const tasksHeading = useRef<HTMLHeadingElement>(null);
  const activityHeading = useRef<HTMLHeadingElement>(null);
  const tasksAnchor = useRef<HTMLDivElement>(null);
  const activityAnchor = useRef<HTMLLIElement>(null);
  const tasksToggle = useRef<HTMLButtonElement>(null);
  const activityToggle = useRef<HTMLButtonElement>(null);
  const collapseHeading = useRef<HTMLHeadingElement | null>(null);
  useLayoutEffect(() => {
    const heading = collapseHeading.current;
    if (!heading) return;
    collapseHeading.current = null;
    heading.scrollIntoView({ block: 'start', behavior: 'instant' });
    heading.focus({ preventScroll: true });
  }, [historyOpen, tasksOpen]);
  useEffect(() => {
    if (!historyOpen && !tasksOpen) return;
    let previousY = window.scrollY;
    let frame = 0;
    const update = () => {
      frame = 0;
      const y = window.scrollY;
      const returningToTop =
        y < previousY &&
        (pageRoot.current?.getBoundingClientRect().top ?? -1) >= 0;
      previousY = y;
      if (returningToTop) {
        setHistoryOpen(false);
        setTasksOpen(false);
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', schedule, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
    };
  }, [historyOpen, tasksOpen]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const demoNow = account
    ? now + account.serverOffset
    : now + state.dayOffset * DAY;
  const today = Math.floor(demoNow / DAY);
  const claimed = account
    ? !!account.data?.daily.claimed
    : connected && state.claims.includes(today);
  const streak = account
    ? (account.data?.daily.streak ?? 0)
    : connected
      ? streakLength(state.claims, today)
      : 0;
  const welcomeClaimed = account
    ? !!account.data?.welcome.claimed
    : connected && state.welcome;
  const shareCompleted = !account && connected && state.shared;
  const completedDays = account
    ? (account.data?.daily.completedDays ?? 0)
    : claimed
      ? ((streak - 1) % 7) + 1
      : streak % 7;
  const firstDay = account
    ? (account.data?.daily.firstDay ?? 1)
    : streak - completedDays + 1;
  const nextReset = account?.data?.nextReset ?? (today + 1) * DAY;
  const seconds = Math.max(0, Math.floor((nextReset - demoNow) / 1000));
  const showNextDay = claimed && completedDays === 7;
  const countdown = [
    Math.floor(seconds / 3600),
    Math.floor(seconds / 60) % 60,
    seconds % 60,
  ]
    .map((value) => String(value).padStart(2, '0'))
    .join(':');
  const claim = (reward: Reward) => {
    if (!connected) return;
    if (account) {
      if (reward !== 'share') account.claim(reward);
      return;
    }
    dispatch({ type: 'claim', reward, day: today });
  };
  const coinModal = useCoinModal();
  const visibleEntries = account
    ? (account.data?.history.entries ?? []).map((e) => {
        const negative = e.amount.startsWith('-');
        return {
          id: e.id,
          title: e.reason,
          day: Math.floor(new Date(e.createdAt).getTime() / DAY),
          createdAt: e.createdAt,
          status:
            e.action === 'correction'
              ? 'Corrected'
              : e.action === 'stake'
                ? 'Placed'
                : e.action === 'refund'
                  ? 'Refunded'
                  : 'Received',
          roundId: e.roundId,
          detail: undefined as string | undefined,
          sign: negative ? -1 : 1,
          formattedAmount: formatAccountPoints(e.amount.replace(/^-/, '')),
        };
      })
    : (connected ? state.entries : []).map((e) => ({
        ...e,
        createdAt: undefined,
        roundId: undefined as string | undefined,
        sign: Math.sign(e.amountCents),
        formattedAmount: formatPoints(Math.abs(e.amountCents)),
      }));
  const entries = historyOpen ? visibleEntries : visibleEntries.slice(0, 3);
  const tasksCollapseFloating = useFloatingCollapse(
    tasksOpen && designTasks.length > 1,
    tasksAnchor,
    tasksToggle,
    designTasks[FLOAT_AFTER_ROWS - 3]?.id,
  );
  const activityCollapseFloating = useFloatingCollapse(
    historyOpen && visibleEntries.length > 3,
    activityAnchor,
    activityToggle,
    entries[FLOAT_AFTER_ROWS - 1]?.id,
  );

  return (
    <section className="rd-points" aria-label="Points" ref={pageRoot}>
      <div className="rd-points-content">
        {account?.error && (
          <p role="alert">
            {account.error}{' '}
            <button onClick={account.refresh} disabled={account.busy}>
              Retry
            </button>
          </p>
        )}

        <div className="rd-points-overview">
          <header className="rd-points-intro">
            <h1>Points</h1>
            <p>
              You have{' '}
              <strong>
                <PointsAmount
                  value={
                    account
                      ? account.formattedBalance
                      : connected
                        ? preview.formattedBalance
                        : '0'
                  }
                />
              </strong>
              . Earn more through daily check-ins, tasks, and winning
              predictions to climb the <strong>weekly leaderboard</strong> and
              compete for rewards.{' '}
              <strong>Your points will matter more in the future</strong>.
            </p>
          </header>
          <section
            className="rd-points-panel rd-points-daily"
            aria-labelledby="daily-title"
          >
            <div className="rd-points-panel-top rd-points-daily-heading">
              <h2 id="daily-title">Check-in</h2>
              {connected && (
                <span className={`rd-points-streak ${streak ? 'active' : ''}`}>
                  <Flame size={16} />
                  {streak} day{streak === 1 ? '' : 's'} streak
                </span>
              )}
            </div>
            <ol
              className={`rd-points-week${showNextDay ? ' with-next-day' : ''}`}
              aria-label={`Days ${firstDay}–${firstDay + (showNextDay ? 7 : 6)} rewards`}
            >
              {Array.from({ length: 7 }, (_, index) => {
                const amount =
                  account?.data?.daily.days[index] ??
                  dailyRewardForDay(firstDay + index);
                const done = index < completedDays;
                const claimDay = !claimed && index === completedDays;
                const current = connected && claimDay;
                const waiting = claimed && index === completedDays;
                return (
                  <li
                    key={firstDay + index}
                    className={`${done ? 'complete' : ''} ${current ? 'current' : ''} ${index === 6 ? 'bonus' : ''}`}
                    aria-current={current ? 'step' : undefined}
                  >
                    <span>Day {firstDay + index}</span>
                    <strong>
                      <PointsAmount value={'+' + number.format(amount)} />
                    </strong>
                    <span
                      className={`rd-points-day-state${waiting ? ' rd-points-day-countdown' : ''}`}
                      aria-label={
                        waiting
                          ? `Day ${firstDay + index} available in ${countdown}`
                          : undefined
                      }
                    >
                      {claimDay ? (
                        <button
                          type="button"
                          className="rd-points-day-claim"
                          aria-label={`Claim Day ${firstDay + index} reward`}
                          aria-busy={account?.busy || undefined}
                          disabled={!connected || account?.busy}
                          onClick={() => claim('daily')}
                        >
                          {account?.busy ? '…' : 'Claim'}
                        </button>
                      ) : waiting ? (
                        countdown
                      ) : done ? (
                        'Claimed'
                      ) : index === 6 ? (
                        'Bonus'
                      ) : (
                        '\u00a0'
                      )}
                    </span>
                  </li>
                );
              })}
              {showNextDay && (
                <li className="rd-points-next-day">
                  <span>Day {firstDay + 7}</span>
                  <strong aria-hidden="true">{'\u00a0'}</strong>
                  <span
                    className="rd-points-day-state rd-points-day-countdown"
                    aria-label={`Day ${firstDay + 7} available in ${countdown}`}
                  >
                    {countdown}
                  </span>
                </li>
              )}
            </ol>
          </section>
        </div>
        <section
          className="rd-points-tasks"
          aria-labelledby="points-tasks-title"
        >
          <div className="rd-points-section-heading">
            <h2 id="points-tasks-title" ref={tasksHeading} tabIndex={-1}>
              Tasks
            </h2>
          </div>
          <div className={`rd-points-task ${welcomeClaimed ? 'done' : ''}`}>
            <div className="rd-points-task-icon">
              <Gift size={23} />
            </div>
            <div className="rd-points-task-copy">
              <h3>Your first move</h3>
              <p>A welcome reward to get you started.</p>
              <span>
                <PointsAmount value={'+' + number.format(WELCOME_REWARD)} />
                <span className="rd-points-task-dot">·</span>One time
              </span>
            </div>
            <button
              className={`rd-points-button ${welcomeClaimed ? 'claimed' : 'rd-points-primary'}`}
              disabled={!connected || welcomeClaimed || account?.busy}
              onClick={() => claim('welcome')}
            >
              {welcomeClaimed ? (
                <>
                  <Check size={16} />
                  Claimed
                </>
              ) : (
                <>
                  Claim
                  <ArrowUpRight size={16} />
                </>
              )}
            </button>
          </div>
          <div className={`rd-points-task ${shareCompleted ? 'done' : ''}`}>
            <div className="rd-points-task-icon">
              <XMark />
            </div>
            <div className="rd-points-task-copy">
              <h3>Spread the word</h3>
              <p>Share Run or Die with your people on X.</p>
              <span>
                <PointsAmount value={'+' + SHARE_REWARD} />
                <span className="rd-points-task-dot">·</span>
                One time
              </span>
            </div>
            <button
              className={`rd-points-button ${shareCompleted ? 'claimed' : 'rd-points-primary'}`}
              disabled={!connected || shareCompleted || !!account}
              title={
                account
                  ? 'Available once post verification is enabled.'
                  : undefined
              }
              onClick={() => setModal('share')}
            >
              {shareCompleted ? (
                <>
                  <Check size={16} />
                  Completed
                </>
              ) : (
                <>
                  {account ? 'Coming soon' : 'Start'}
                  <ArrowUpRight size={16} />
                </>
              )}
            </button>
          </div>
          {designTasks.length > 0 && (
            <div id="points-design-tasks">
              {(tasksOpen ? designTasks : designTasks.slice(0, 1)).map(
                (task, index) => (
                  <div
                    className="rd-points-task"
                    key={task.id}
                    ref={
                      index === FLOAT_AFTER_ROWS - 3 ? tasksAnchor : undefined
                    }
                  >
                    <div className="rd-points-task-icon" aria-hidden="true">
                      {task.icon === 'first-bet' ? (
                        <span className="rd-points-task-number">1</span>
                      ) : task.icon === 'x' ? (
                        <XMark />
                      ) : task.icon === 'profile' ? (
                        <UserRound size={23} />
                      ) : (
                        <UsersRound size={23} />
                      )}
                    </div>
                    <div className="rd-points-task-copy">
                      <h3>{task.title}</h3>
                      <p>{task.description}</p>
                      <span>
                        <PointsAmount
                          value={'+' + number.format(task.reward)}
                        />
                        <span className="rd-points-task-dot">·</span>One time
                      </span>
                    </div>
                    <button
                      className="rd-points-button rd-points-primary"
                      type="button"
                      disabled
                    >
                      {task.action}
                      <ArrowUpRight size={16} />
                    </button>
                  </div>
                ),
              )}
            </div>
          )}
          <button
            type="button"
            className="rd-points-tasks-more"
            ref={tasksToggle}
            data-collapse-floating={tasksCollapseFloating}
            disabled={designTasks.length <= 1}
            aria-expanded={designTasks.length > 1 ? tasksOpen : undefined}
            aria-controls={
              designTasks.length > 1 ? 'points-design-tasks' : undefined
            }
            onClick={() => {
              if (tasksOpen) collapseHeading.current = tasksHeading.current;
              setTasksOpen(!tasksOpen);
            }}
          >
            {tasksOpen ? 'Show less' : 'View all tasks'}
            <ChevronDown size={15} />
          </button>
          {!account && (
            <details className="rd-points-preview">
              <summary>
                <span className="rd-preview-dot" />
                Design preview
                <ChevronDown size={14} />
              </summary>
              <div className="rd-preview-popover">
                <strong>Try the different states</strong>
                <p>
                  {environment === 'live'
                    ? 'Demo points only. This preview does not fund live predictions. Loading a scenario or resetting changes only this points preview.'
                    : 'Demo points only. Loading a scenario or resetting starts a fresh Arena and points session.'}{' '}
                  Changes last until you reload.
                </p>
                <label>
                  Load a scenario
                  <select
                    value=""
                    onChange={(event) =>
                      dispatch({
                        type: 'preset',
                        preset: event.target.value,
                        day: Math.floor(now / DAY),
                      })
                    }
                  >
                    <option value="" disabled>
                      Choose a state…
                    </option>
                    <option value="new">First visit</option>
                    <option value="ready">Rewards ready</option>
                    <option value="claimed">Rewards claimed</option>
                    <option value="streak">Day 7 · bonus ready</option>
                  </select>
                </label>
                <button
                  disabled={!connected}
                  onClick={() => dispatch({ type: 'next-day' })}
                >
                  <Clock3 size={15} />
                  Next demo day
                  <ArrowRight size={15} />
                </button>
                <button onClick={() => dispatch({ type: 'reset' })}>
                  <RotateCcw size={15} />
                  Reset preview
                </button>
              </div>
            </details>
          )}
        </section>
        <section
          className="rd-points-history"
          aria-labelledby="points-activity-title"
        >
          <div className="rd-points-section-heading">
            <h2 id="points-activity-title" ref={activityHeading} tabIndex={-1}>
              Activity
            </h2>
          </div>
          {entries.length ? (
            <>
              <ul id="points-activity-list">
                {entries.map((entry, index) => (
                  <li
                    key={entry.id}
                    ref={
                      index === FLOAT_AFTER_ROWS - 1
                        ? activityAnchor
                        : undefined
                    }
                  >
                    <span className="rd-points-history-icon">
                      {entry.sign < 0 ? (
                        <ArrowUpRight size={16} />
                      ) : entry.sign === 0 ? (
                        <History size={16} />
                      ) : (
                        <ArrowDownLeft size={16} />
                      )}
                    </span>
                    <div>
                      <strong>{entry.title}</strong>
                      <span>
                        {entry.createdAt ? (
                          <time dateTime={entry.createdAt}>
                            {activityDate.format(new Date(entry.createdAt))}
                            {' · '}
                            {activityTime.format(new Date(entry.createdAt))}
                          </time>
                        ) : entry.day === today ? (
                          'Today'
                        ) : entry.day === today - 1 ? (
                          'Yesterday'
                        ) : (
                          `${today - entry.day} days ago`
                        )}{' '}
                        · {entry.status}
                      </span>
                      {entry.detail && (
                        <span>
                          <PointsText text={entry.detail} />
                        </span>
                      )}
                      {entry.roundId && (
                        <button
                          type="button"
                          onClick={(event) =>
                            coinModal?.openRound(
                              entry.roundId!,
                              event.currentTarget,
                            )
                          }
                        >
                          View round
                        </button>
                      )}
                    </div>
                    <strong>
                      <PointsAmount
                        value={
                          (entry.sign > 0 ? '+' : entry.sign < 0 ? '−' : '') +
                          entry.formattedAmount
                        }
                      />
                    </strong>
                  </li>
                ))}
              </ul>
              {account && historyOpen && account.data?.history.nextCursor && (
                <button
                  className="rd-points-history-more"
                  disabled={account.busy}
                  onClick={account.loadMore}
                >
                  Load more
                </button>
              )}
              {visibleEntries.length > 3 && (
                <button
                  className="rd-points-history-more"
                  data-collapse-floating={activityCollapseFloating}
                  ref={activityToggle}
                  aria-expanded={historyOpen}
                  aria-controls="points-activity-list"
                  onClick={() => {
                    if (historyOpen)
                      collapseHeading.current = activityHeading.current;
                    setHistoryOpen(!historyOpen);
                  }}
                >
                  {historyOpen ? 'Show less' : 'View all activity'}
                  <ChevronDown size={15} />
                </button>
              )}
            </>
          ) : (
            <div className="rd-points-history-empty">
              <span>
                <History size={23} />
              </span>
              <strong>{connected ? 'A fresh start.' : 'Your activity'}</strong>
              <p>
                {connected ? (
                  <>
                    Your rewards and predictions will
                    <br />
                    show up here.
                  </>
                ) : (
                  'Your rewards and predictions will appear here.'
                )}
              </p>
            </div>
          )}
        </section>
      </div>
      {account?.data?.debt && (
        <p role="status">
          Result correction: {formatAccountPoints(account.data.debt)} PTS to
          recover. Future earnings reduce this amount; available balance is
          zero.
        </p>
      )}
      <p className="rd-points-sr-only" role="status" aria-live="polite">
        {connected ? state.message : ''}
      </p>

      {!account && modal === 'share' && (
        <Modal label="Preview sharing task" close={() => setModal(null)}>
          <div className="rd-points-modal">
            <span className="rd-points-modal-icon">
              <XMark />
            </span>
            <span className="rd-points-modal-eyebrow">DESIGN PREVIEW</span>
            <h2>Good calls deserve company.</h2>
            <p>
              This previews the sharing reward. Nothing is posted to X, and no
              account is connected.
            </p>
            <div className="rd-points-share-preview">
              <span>POST PREVIEW</span>
              <p>
                Run or Die. A new token. One call.
                <br />
                Think you can read the next move?
              </p>
              <span>runordie.xyz</span>
            </div>
            <button
              className="rd-points-button rd-points-primary"
              onClick={() => {
                claim('share');
                setModal(null);
              }}
            >
              Simulate completed task
              <ArrowRight size={18} />
            </button>
            <span className="rd-points-modal-note">
              Example reward: <PointsAmount value={'+' + SHARE_REWARD} />
            </span>
          </div>
        </Modal>
      )}
    </section>
  );
}
